import { useEffect, useState } from "react";
import {
  fetchDefaultAutoTaggingPlanPrompt,
  type FetchDefaultAutoTaggingPlanPromptOptions,
} from "../services/autoTaggingService";

export interface UseDefaultAssessmentPromptResult {
  defaultPrompt: string;
  loading: boolean;
  error: string | null;
}

export function useDefaultAssessmentPrompt(
  conceptCode: string | undefined,
  options?: FetchDefaultAutoTaggingPlanPromptOptions,
): UseDefaultAssessmentPromptResult {
  const normalizedConceptCode = conceptCode?.trim() ?? "";
  const targetType = options?.targetType?.trim() ?? "";
  const subType = options?.subType?.trim() ?? "";
  const assessmentType = options?.assessmentType?.trim() ?? "";
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!normalizedConceptCode) {
      setDefaultPrompt("");
      setLoading(false);
      setError(null);
      return;
    }

    let mounted = true;
    // Clear immediately so the field shows a loading overlay instead of a
    // blank or stale placeholder while the default prompt is refetching.
    setLoading(true);
    setDefaultPrompt("");
    setError(null);

    fetchDefaultAutoTaggingPlanPrompt(normalizedConceptCode, {
      targetType,
      subType,
      assessmentType,
    })
      .then((prompt) => {
        if (!mounted) return;
        setDefaultPrompt(prompt);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setDefaultPrompt("");
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load default assessment prompt.",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [normalizedConceptCode, targetType, subType, assessmentType]);

  return {
    defaultPrompt,
    loading,
    error,
  };
}
